import { addDays, endOfDay, startOfDay } from 'date-fns';
import { useSubGoals } from 'features/goals';
import { useTasks } from 'features/tasks';
import { useMemo } from 'react';
import { useToday } from 'shared/contexts/today';
import { Goal } from 'shared/types/goal';
import { DefaultMetricOptions } from 'shared/types/metric-template';

export const useTimeframeTimes = (goal?: Goal) => {
  const today = useToday();

  const subGoals = useSubGoals(goal?.id, true);
  // only read for completed-tasks metrics, and only completedAt — so fetch completed-only, else skip
  const allTasks = useTasks(
    {
      goals: [goal?.id!, ...subGoals.map(({ id }) => id)],
      completedOnly: true,
    },
    !!goal?.id &&
      (!goal?.metric || goal.metric.id === DefaultMetricOptions.CompletedTasks),
  );

  const firstCompletionDate = useMemo(() => {
    const completedTaskTimes = allTasks
      .filter(({ completedAt }) => !!completedAt)
      .map(({ completedAt }) => completedAt!.getTime());

    return completedTaskTimes.length &&
      goal &&
      (goal?.metric?.id === DefaultMetricOptions.CompletedTasks || !goal.metric)
      ? new Date(Math.min(...completedTaskTimes))
      : undefined;
  }, [allTasks, goal]);

  const lastCompletionDate = useMemo(
    () =>
      goal &&
      (goal?.metric?.id === DefaultMetricOptions.CompletedTasks || !goal.metric)
        ? new Date(
            Math.max(
              ...allTasks
                .filter(({ completedAt }) => !!completedAt)
                .map(({ completedAt }) => completedAt!.getTime()),
            ),
          )
        : undefined,
    [allTasks, goal],
  );

  const firstMetricEntryDate = useMemo(
    () =>
      goal?.metric?.entries?.length
        ? new Date(
            Math.min(...goal.metric.entries.map(({ date }) => date.getTime())),
          )
        : undefined,
    [goal?.metric?.entries],
  );
  const lastMetricEntryDate = useMemo(
    () =>
      goal?.metric?.entries?.length
        ? new Date(
            Math.max(...goal.metric.entries.map(({ date }) => date.getTime())),
          )
        : undefined,
    [goal?.metric?.entries],
  );

  const startDate = useMemo(
    () =>
      firstCompletionDate ||
      firstMetricEntryDate ||
      goal?.startDate ||
      goal?.deadline
        ? startOfDay(
            new Date(
              Math.min(
                ...([
                  firstCompletionDate?.getTime(),
                  firstMetricEntryDate?.getTime(),
                  goal?.deadline?.getTime(),
                  goal?.startDate?.getTime(),
                ].filter(Boolean) as number[]),
              ),
            ),
          )
        : addDays(today, -30),
    [
      firstCompletionDate,
      firstMetricEntryDate,
      goal?.deadline,
      goal?.startDate,
      today,
    ],
  );

  const endDate = useMemo(() => {
    const timestamps = [
      lastCompletionDate?.getTime(),
      lastMetricEntryDate?.getTime(),
      goal?.deadline?.getTime(),
      goal?.startDate ? addDays(goal.startDate, 30).getTime() : undefined,
      !goal?.completedAt ? addDays(today, goal?.deadline ? 0 : 30) : undefined,
    ].filter(Boolean) as number[];
    return endOfDay(
      new Date(timestamps.length ? Math.max(...timestamps) : today.getTime()),
    );
  }, [
    goal?.completedAt,
    goal?.deadline,
    goal?.startDate,
    lastCompletionDate,
    lastMetricEntryDate,
    today,
  ]);

  return useMemo(
    () => ({
      startDate,
      endDate,
    }),
    [endDate, startDate],
  );
};
